import { ReactNode, useEffect, useState } from 'react';

import * as api from '@/features/backlog/api';
import { Button } from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';
import { Check, Download, Duplicate } from '@/components/ui/icons';
import { useIdentity } from './identity';

/** Static path of the bundle committed under frontend/public (kept in sync by mcp/scripts/bundle.mjs). */
const BUNDLE_HREF = '/spark-board.mcpb';
const BUNDLE_NAME = 'spark-board.mcpb';

/* The two fields Claude Desktop's extension form asks for — see mcp/manifest.json's user_config.
   apiUrl is the same runtime value the app itself talks to, so it's exactly right to paste. */
const apiUrl = () => (typeof window !== 'undefined' && window.config?.apiUrl) || '';

/** A labelled value with a one-tap copy button — so the API URL / email can be pasted, not retyped. */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked (e.g. insecure origin) — the value is on screen to copy by hand */
    }
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-line bg-layer px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-fg-faint">{label}</div>
        <div className="truncate font-mono text-[12px] text-fg-strong" title={value}>
          {value}
        </div>
      </div>
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy ${label}`}
        // tap-target lifts the hit area to >=48px without growing the visible 36px disc.
        className="tap-target flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
      >
        {copied ? <Check className="text-brand" /> : <Duplicate />}
      </button>
      {/* Announce success to assistive tech — a swapped aria-hidden icon alone conveys nothing. */}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? `${label} copied to clipboard` : ''}
      </span>
    </div>
  );
}

/**
 * Your personal MCP API key — owner-scoped: fetched on open, shown so YOU can copy it into the
 * connector config, and rotatable. Nobody else (not even admins) can read it. Non-admins get
 * read-only MCP access with their key; admins get read + write.
 */
function ApiKeyField({ isAdmin }: { isAdmin: boolean }) {
  const [key, setKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getMyApiKey()
      .then((k) => {
        if (!cancelled) setKey(k || null);
      })
      .catch(() => {
        /* leave null; the Generate button lets them mint one */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const mint = async () => {
    setBusy(true);
    try {
      const k = await api.regenerateMyApiKey();
      setKey(k);
      setConfirmRotate(false);
    } catch {
      /* ignore — button stays available to retry */
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-line bg-layer px-3 py-2 text-[12px] text-fg-faint">
        <Spinner /> Loading your key…
      </div>
    );
  }
  if (!key) {
    return (
      <div className="rounded-lg border border-line bg-layer px-3 py-3">
        <p className="mb-2 text-[12px] text-fg-soft">You don’t have an API key yet. Generate one to connect the MCP.</p>
        <Button type="button" variant="primary" onClick={mint} disabled={busy}>
          {busy ? <Spinner /> : 'Generate API key'}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <CopyField label="Your API key" value={key} />
      <div className="flex items-center gap-2 px-1 text-[11px] text-fg-faint">
        <span>{isAdmin ? 'Full read + write access via MCP.' : 'Read-only access via MCP (admins can write).'}</span>
        <span className="text-fg-ghost">·</span>
        {confirmRotate ? (
          <button type="button" onClick={mint} disabled={busy} className="font-medium text-danger hover:underline">
            {busy ? 'Rotating…' : 'Click again to confirm — this invalidates the current key'}
          </button>
        ) : (
          <button type="button" onClick={() => setConfirmRotate(true)} className="font-medium text-fg-muted hover:text-fg hover:underline">
            Regenerate
          </button>
        )}
      </div>
    </div>
  );
}

/** A numbered instruction step. */
function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-layer-strong text-[12px] font-semibold text-fg-soft">
        {n}
      </span>
      <span className="text-[13px] leading-relaxed text-fg-soft">{children}</span>
    </li>
  );
}

/**
 * Instructions for adding the spark-board MCP connector to Claude Desktop, with a one-click
 * download of the prebuilt `.mcpb` bundle. Opened from the account menu. The steps mirror
 * mcp/README.md; the copy fields are prefilled from the running app so setup is paste-only.
 */
export default function SetupMcpModal({
  open,
  onClose,
  returnFocusTo,
}: {
  open: boolean;
  onClose: () => void;
  /** The account trigger to restore focus to on close (the menu row that opened this is gone). */
  returnFocusTo?: { readonly current: HTMLElement | null };
}) {
  const { me } = useIdentity();
  const isAdmin = me?.role === 'admin';
  // The latest connector version (published next to the bundle by mcp/scripts/bundle.mjs), so people
  // can tell what's current and name their download by version. Degrades silently if not served.
  const [ver, setVer] = useState<{ version: string; built?: string } | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetch('/spark-board-mcp-version.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => {
        if (!cancelled && v && typeof v.version === 'string') setVer(v);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open]);
  const downloadName = ver ? `spark-board-${ver.version}.mcpb` : BUNDLE_NAME;
  return (
    <Modal
      open={open}
      onClose={onClose}
      returnFocusTo={returnFocusTo}
      title={<h2 className="text-[15px] font-semibold text-fg-strong">Set up the MCP connector</h2>}
    >
      <div className="flex flex-col gap-6">
        {/* Hero: the download is the primary action. */}
        <div className="flex flex-col items-start gap-3 rounded-card border border-line bg-layer/60 p-4">
          <p className="text-[13px] leading-relaxed text-fg-soft">
            Add this backlog to Claude Desktop so you can search and manage items straight from chat.
            Grab the one-file bundle below — no Node install or checkout needed; the compiled connector
            and its dependencies are all inside.
          </p>
          <a
            href={BUNDLE_HREF}
            download={downloadName}
            className={cn(
              'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-5 text-[13px] font-semibold transition-colors',
              'bg-brand text-black hover:bg-brand-hover',
            )}
          >
            <Download />
            Download {downloadName}
          </a>
          {ver ? (
            <p className="text-[11px] leading-relaxed text-fg-faint">
              Latest connector <b className="font-semibold text-fg-soft">v{ver.version}</b>
              {ver.built ? ` · built ${new Date(ver.built).toLocaleDateString()}` : ''}. Already have an older
              one installed? Re-download and reinstall to update — run <b className="text-fg-soft">whoami</b> in
              chat to check your version.
            </p>
          ) : null}
        </div>

        {/* Steps — mirror mcp/README.md's "Install for colleagues" flow. */}
        <div>
          <h3 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-fg-faint">
            Then, in Claude Desktop
          </h3>
          <ol className="flex flex-col gap-3">
            <Step n={1}>
              Open <b className="font-semibold text-fg">Settings → Extensions → Install Extension…</b>{' '}
              and pick the file you just downloaded (or drag it onto the window).
            </Step>
            <Step n={2}>
              When the setup form appears, paste your <b className="font-semibold text-fg">API URL</b>{' '}
              and <b className="font-semibold text-fg">API key</b> from below, then enable it.
            </Step>
            <Step n={3}>
              Claude notes the extension is <b className="font-semibold text-fg">unsigned</b> — that's
              expected for an internal bundle. Approve it, and the spark-board tools appear in your chat.
            </Step>
          </ol>
        </div>

        {/* Prefilled config — the exact two values the extension form requires. */}
        <div>
          <h3 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-fg-faint">
            Paste these when asked
          </h3>
          <div className="flex flex-col gap-2">
            <CopyField label="spark-board API URL" value={apiUrl()} />
            <ApiKeyField isAdmin={isAdmin} />
          </div>
          <p className="mt-2 px-1 text-[11px] leading-relaxed text-fg-faint">
            Your API key is personal and secret — only you can see it here. It authenticates the MCP as
            you, so don’t share it. Regenerate it if it ever leaks.
          </p>
        </div>

        <p className="text-[12px] leading-relaxed text-fg-faint">
          On the company network you must be on the VPN for Claude to reach the backend. Prefer Claude
          Code or running from source? See <span className="font-mono text-fg-muted">mcp/README.md</span>{' '}
          in the repo.
        </p>
      </div>
    </Modal>
  );
}
